'use client';

import React, { useState } from 'react';
import GenerateButton from '@/components/ui/GenerateButton';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';

interface DateOptions {
    startDate: string;
    endDate: string;
    count: number;
    includeTime: boolean;
}

interface GeneratedDate {
    unix: number;
    iso: string;
    utc: string;
    local: string;
}

function GenerateDatesPage() {
    const [options, setOptions] = useState<DateOptions>({
        startDate: '1970-01-01',
        endDate: new Date().toISOString().split('T')[0],
        count: 1,
        includeTime: true,
    });

    const [generatedDates, setGeneratedDates] = useState<GeneratedDate[]>([]);

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
        }));
    };

    const generateDates = () => {
        if (options.count < 1 || options.count > 100) {
            return;
        }
        if (new Date(options.startDate) > new Date(options.endDate)) {
            return;
        }
        const start = new Date(options.startDate).getTime();
        const end = new Date(options.endDate).getTime();
        const newDates: GeneratedDate[] = [];

        for (let i = 0; i < options.count; i++) {
            const randomTime = Math.random() * (end - start) + start;
            const date = new Date(randomTime);

            if (!options.includeTime) {
                date.setHours(0, 0, 0, 0);
            }

            newDates.push({
                unix: Math.floor(date.getTime() / 1000),
                iso: date.toISOString(),
                utc: date.toUTCString(),
                local: date.toLocaleString(),
            });
        }

        setGeneratedDates(newDates);
    };

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Random Date Generator</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Options</h2>
                        <label className="label">
                            <span className="label-text">Start Date</span>
                        </label>
                        <input 
                            type="date" 
                            className={`input input-bordered w-full ${options.startDate > options.endDate ? 'input-error' : ''}`}
                            name="startDate" 
                            value={options.startDate} 
                            onChange={handleOptionChange} 
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">End Date</span>
                        </label>
                        <input 
                            type="date" 
                            className={`input input-bordered w-full ${options.endDate < options.startDate ? 'input-error' : ''}`}
                            name="endDate" 
                            value={options.endDate} 
                            onChange={handleOptionChange} 
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Number of Dates to Generate (1-100)</span>
                        </label>
                        <input 
                            type="number" 
                            className={`input input-bordered w-full ${options.count < 1 || options.count > 100 ? 'input-error' : ''}`}
                            name="count" 
                            min="1" 
                            max="100"
                            value={options.count} 
                            onChange={handleOptionChange} 
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label cursor-pointer justify-start">
                            <input 
                                type="checkbox" 
                                className="checkbox checkbox-sm mr-2" 
                                name="includeTime" 
                                checked={options.includeTime} 
                                onChange={handleOptionChange} 
                            />
                            <span className="label-text">Include Time</span>
                        </label>
                    </div>
                    <GenerateButton generatePasswords={generateDates} />
                </div>

                {/* Right Column - Generated Dates */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Generated Dates</h2>
                    <AutoGrowingTextarea
                        value={generatedDates.map(date => 
                            `Unix Epoch: ${date.unix}\n` +
                            `ISO 8601: ${date.iso}\n` +
                            `UTC: ${date.utc}\n` +
                            `Local: ${date.local}\n\n`
                        ).join('')}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateDatesPage;